// Explicit, as ~10 other components here do: the test babel env transforms JSX with a bare `h`
// pragma and does not inject the import, so without this the component cannot be rendered in jest.
import { h } from 'preact';

import { useIsMobileSize } from '@/hooks';
import { useSelector } from '@/redux/helper';
import { selectHasGuidedExperienceFeature } from '@/redux/selectors/users';
import { selectTourStatus } from '@/redux/slices/tours/selectors';

import { TZ_PLUS_ID } from '../config/tzPlus/ids';
import { isClearedStatus } from '../core/status';
import useHasTzPlusFeature from '../hooks/useHasTzPlusFeature';
import { GUIDED_JOURNEY_ID } from '../registry';
import MobileWarningModal from './ui/MobileWarningModal';
import TzPlusMobileWelcome from './ui/TzPlusMobileWelcome';

/**
 * The "you want a desktop for this" gate, for users entitled to a tour they cannot run on a phone.
 * Deliberately NOT a tour step: it applies whether or not a tour is running, so it cannot be driven
 * by a step's ui config. Mounted globally, outside the Router.
 *
 * When a user holds both entitlements the TZ+ message wins, matching the order the tours themselves
 * run in (see registry.ts and docs/adr/0012) — one message at a time, and the one for the tour they
 * would be shown first.
 */
const TourMobileGate = () => {
  const isMobile = useIsMobileSize();
  const isLoggedIn = useSelector((state) => state.user.logged);
  const hasGuidedExperience = useSelector(selectHasGuidedExperienceFeature);
  const hasTzPlus = useHasTzPlusFeature();
  // A tour that is out of the way should stop nagging, however it got there. `active` is
  // emphatically NOT out of the way — a tour started on a desktop and half-finished is exactly who
  // these messages are for — so they keep coming back, once per session, until the tour is done.
  const tzPlusCleared = isClearedStatus(useSelector(selectTourStatus(TZ_PLUS_ID)));
  const journeyCleared = isClearedStatus(useSelector(selectTourStatus(GUIDED_JOURNEY_ID)));

  // One slot, one occupant: which message shows is a choice made here, so no future edit can put
  // two on screen at once. TZ+ wins while it is still in play, matching the order the tours
  // themselves run in (see registry.ts and docs/adr/0012). Both branches are gated the same way,
  // so the two messages cannot drift apart on when they retire.
  const Modal =
    !isLoggedIn || !isMobile
      ? null
      : (hasTzPlus && !tzPlusCleared && TzPlusMobileWelcome) ||
        (hasGuidedExperience && !journeyCleared && MobileWarningModal) ||
        null;

  return Modal ? <Modal /> : null;
};

export default TourMobileGate;
